/**
 * The classical tables — rāśi, graha, nakṣatra, bhāva
 *
 * Every number and every classification in this file is **cited**, and the
 * citation names a section, not a vibe. The primary source is on disk:
 *
 *     the classical text, *the Horā tradition*
 *     → refs/textbook.txt (gitignored — his copyright)
 *
 * Section numbers below (§2.2.4, §3.2.15, Table 6 …) are that book's.
 *
 * ── Why the tables are written as GROUPS and not as 12 rows ─────────────────
 *
 * the classical text states these classifications the way the śāstra does: "Ar, Cn, Li and Cp
 * are chara rāśis." He does not print a 12 × 12 attribute grid. So neither do
 * we. Each classification is encoded as the source's own list, and the per-rāśi
 * object is *derived* by `byGroup()`, which throws at module load if any index
 * is missing or claimed twice.
 *
 * ◆ That is a deliberate trade. A hand-transcribed grid of 12 rows × 12 columns
 * is 144 chances to shift a cell, and a shifted cell yields a chart that is
 * wrong in one attribute and right in every other — invisible. Encoding the
 * source's sentences and deriving the grid means a transcription slip becomes a
 * **load-time exception**, not a subtly wrong reading.
 *
 * ── ⚠ Four places where the source disagrees with itself or with the tradition
 *
 * All four are recorded at their definition site rather than silently
 * "corrected". See TABLE_DISCREPANCIES at the end of this file for the list a
 * later phase has to settle.
 *
 * ⚠ **`GRAHAS` here is nine objects. `GRAHAS` in src/astro/ephemeris.js is
 * seven strings** — the physical bodies, which is all an ephemeris can know.
 * Never import both into one scope under one name; alias one of them.
 *
 * © Manish Jagdish Thatte
 */

/* ──────────────────────────────────────────────────────────────────────────
 * Geometry
 * ────────────────────────────────────────────────────────────────────────── */

/** A rāśi is 30°. Table 1. */
export const RASHI_SPAN_DEG = 30;

/** A nakṣatra is 360/27 = 13°20′. Table 2. */
export const NAKSHATRA_SPAN_DEG = 360 / 27;

/** A pada is a quarter nakṣatra, 3°20′. §1.3.6. */
export const PADA_SPAN_DEG = NAKSHATRA_SPAN_DEG / 4;

export const norm360 = (d) => ((d % 360) + 360) % 360;

/**
 * ── The boundary epsilon, and why it is not paranoia ───────────────────────
 *
 * A nakṣatra is 13°20′ and a pada is 3°20′. Neither 40/3 nor 10/3 is
 * representable in binary floating point, so a boundary can be *named* two ways
 * that are not the same double:
 *
 *     360 / 27      = 13.333333333333334
 *     13 + 20 / 60  = 13.333333333333332
 *
 * ⚠ The second is one bit **below** the first. So `floor(lon / span)` on a
 * longitude written as 13°20′00″ returns 0 — Aśvinī pada 4 — where the project's
 * own boundary rule says 13°20′00″ is **Bharaṇī pada 1**. The division rule and
 * the arithmetic disagree, and the answer that comes out is a plausible
 * neighbouring nakṣatra, which for the Moon means a different daśā lord for the
 * whole life.
 *
 * ◆ `divisionIndex` closes it by adding one part in 10⁹ of a division before
 * flooring. That is 4.8 × 10⁻⁵ arc-seconds for a nakṣatra — five orders of
 * magnitude below anything this project can resolve, and four below the
 * arc-second tolerances in tests/fixtures — so it can only ever affect a
 * longitude that *is* a boundary, approached from below. It cannot move a real
 * position across a real boundary.
 */
const BOUNDARY_EPS = 1e-9;

/**
 * Which of `count` equal divisions a longitude falls in, and how far into it.
 *
 * ◆ One implementation for rāśi, nakṣatra and pada. Three separate `Math.floor`
 * calls would be three chances to get the boundary rule right, and the pada one
 * was already wrong.
 *
 * ⚠ **The index and the offset have to be derived from the same snapped value.**
 * A longitude a fraction of a nano-degree below 360° — which a real graha at the
 * end of Revatī produces — snaps to span number `count`, i.e. off the top of the
 * circle. The *index* must wrap to 0, but the *offset* must then be measured
 * from 360°, not from 0°, or it comes back as the entire longitude: 359.999…°
 * into a 13°20′ nakṣatra, and an elapsed fraction of 27 instead of 0. Computing
 * the offset from the already-wrapped index is exactly that bug, and it is why
 * these two are returned together rather than as two independent functions.
 */
function divisionAt(lon, count) {
  if (!Number.isFinite(lon)) throw new TypeError('divisionAt: longitude must be finite');
  const L = norm360(lon);
  const span = 360 / count;
  // 0 … count inclusive: `count` means the epsilon carried us past 360°.
  const snapped = Math.floor(L * count / 360 + BOUNDARY_EPS);
  return {
    index: snapped % count,
    // Clamped at 0: the snap leaves the subtraction a few ulps negative.
    into: Math.max(0, L - snapped * span)
  };
}

/**
 * Which of `count` equal divisions a longitude falls in, 0-based, with a
 * boundary longitude belonging to the **later** division (A5).
 */
export function divisionIndex(lon, count) {
  return divisionAt(lon, count).index;
}

/**
 * Degrees advanced into a division, ≥ 0 and < the division's span.
 *
 * A boundary longitude that snapped up to the later division reports 0° into it,
 * not 13°19′59.99″ into the earlier one.
 */
export function degreesIntoDivision(lon, count) {
  return divisionAt(lon, count).into;
}

/**
 * Which rāśi a **sidereal** longitude falls in. 0-based: 0 = Meṣa.
 *
 * ⚠ Sidereal. Everything in src/jyotish/ is sidereal; the ayanāṁśa was
 * subtracted once, in src/astro/ayanamsa.js. Handing this a tropical longitude
 * produces a chart roughly 24° — four fifths of a rāśi — out of place.
 *
 * ◆ A boundary longitude belongs to the **later** division (A5). 30°00′00″ is
 * Vṛṣabha, not Meṣa.
 */
export function rashiIndexOf(siderealLon) {
  return divisionIndex(siderealLon, 12);
}

/** Degrees advanced into the occupied rāśi, 0 ≤ d < 30. */
export function degreesInRashi(siderealLon) {
  return degreesIntoDivision(siderealLon, 12);
}

/* ──────────────────────────────────────────────────────────────────────────
 * byGroup — the honesty mechanism
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Invert `{ label: [indices] }` into `[value per index]`, refusing anything
 * that is not a clean partition of `0 … size−1`.
 *
 * ◆ This is the whole reason the classifications below are safe to trust. A
 * classification that does not cover every rāśi, or that claims one twice, is a
 * transcription error by definition — the śāstra's categories partition the
 * zodiac — so it becomes a thrown Error at import time.
 */
function byGroup(size, groups, what) {
  const out = new Array(size).fill(undefined);
  for (const [label, indices] of Object.entries(groups)) {
    for (const i of indices) {
      if (!Number.isInteger(i) || i < 0 || i >= size) {
        throw new RangeError(`${what}: index ${i} out of range 0…${size - 1} in group "${label}"`);
      }
      if (out[i] !== undefined) {
        throw new Error(`${what}: index ${i} claimed by both "${out[i]}" and "${label}"`);
      }
      out[i] = label;
    }
  }
  const missing = out.map((v, i) => (v === undefined ? i : -1)).filter((i) => i >= 0);
  if (missing.length) {
    throw new Error(`${what}: no group covers ${missing.join(', ')} — the partition is incomplete`);
  }
  return out;
}

/* ──────────────────────────────────────────────────────────────────────────
 * The twelve rāśis
 * ────────────────────────────────────────────────────────────────────────── */

const RASHI_IDS = [
  'mesha', 'vrishabha', 'mithuna', 'karka', 'simha', 'kanya',
  'tula', 'vrishchika', 'dhanu', 'makara', 'kumbha', 'mina'
];

/** the classical two-letter symbols, Table 1. Used when quoting the source. */
const RASHI_ABBR = ['Ar', 'Ta', 'Ge', 'Cn', 'Le', 'Vi', 'Li', 'Sc', 'Sg', 'Cp', 'Aq', 'Pi'];

const RASHI_NAMES = [
  { en: 'Meṣa',      sa: 'मेष',     hi: 'मेष',     mr: 'मेष',     western: 'Aries' },
  { en: 'Vṛṣabha',   sa: 'वृषभ',    hi: 'वृषभ',    mr: 'वृषभ',    western: 'Taurus' },
  { en: 'Mithuna',   sa: 'मिथुन',   hi: 'मिथुन',   mr: 'मिथुन',   western: 'Gemini' },
  { en: 'Karka',     sa: 'कर्क',    hi: 'कर्क',    mr: 'कर्क',    western: 'Cancer' },
  { en: 'Siṁha',     sa: 'सिंह',    hi: 'सिंह',    mr: 'सिंह',    western: 'Leo' },
  { en: 'Kanyā',     sa: 'कन्या',   hi: 'कन्या',   mr: 'कन्या',   western: 'Virgo' },
  { en: 'Tulā',      sa: 'तुला',    hi: 'तुला',    mr: 'तुळा',    western: 'Libra' },
  { en: 'Vṛścika',   sa: 'वृश्चिक', hi: 'वृश्चिक', mr: 'वृश्चिक', western: 'Scorpio' },
  { en: 'Dhanu',     sa: 'धनुः',    hi: 'धनु',     mr: 'धनु',     western: 'Sagittarius' },
  { en: 'Makara',    sa: 'मकर',     hi: 'मकर',     mr: 'मकर',     western: 'Capricorn' },
  { en: 'Kumbha',    sa: 'कुम्भ',   hi: 'कुम्भ',   mr: 'कुंभ',    western: 'Aquarius' },
  { en: 'Mīna',      sa: 'मीन',     hi: 'मीन',     mr: 'मीन',     western: 'Pisces' }
];

/** Rāśi lords. Table 1 / §3.3 Table 6 "Own rasis". */
const RASHI_LORD = [
  'mars', 'venus', 'mercury', 'moon', 'sun', 'mercury',
  'venus', 'mars', 'jupiter', 'saturn', 'saturn', 'jupiter'
];

/** §2.2.4 — movable / fixed / dual. */
const RASHI_MOBILITY = byGroup(12, {
  cara:        [0, 3, 6, 9],
  sthira:      [1, 4, 7, 10],
  dvisvabhava: [2, 5, 8, 11]
}, 'rāśi mobility (§2.2.4)');

/** §2.2.5 — the five elements. Ākāśa is in every rāśi and so is not a group. */
const RASHI_TATTVA = byGroup(12, {
  agni:    [0, 4, 8],
  bhu:     [1, 5, 9],
  vayu:    [2, 6, 10],
  jala:    [3, 7, 11]
}, 'rāśi tattva (§2.2.5)');

/** §2.2.2 — odd/male (viṣama, oja) and even/female (sama, yugma). */
const RASHI_GENDER = byGroup(12, {
  male:   [0, 2, 4, 6, 8, 10],
  female: [1, 3, 5, 7, 9, 11]
}, 'rāśi gender (§2.2.2)');

/**
 * §2.2.3 — odd-footed (viṣamapada) and even-footed (samapada).
 *
 * ⚠ **This is not the odd/even split and cannot be derived from the index.**
 * Ar, Ta, Ge, Li, Sc, Sg are odd-footed; Cn, Le, Vi, Cp, Aq, Pi are
 * even-footed — two runs of three, not an alternation. Several daśās use it,
 * and "obviously it's just the odd signs" is the error waiting to be made.
 */
const RASHI_FOOTED = byGroup(12, {
  odd:  [0, 1, 2, 6, 7, 8],
  even: [3, 4, 5, 9, 10, 11]
}, 'rāśi footedness (§2.2.3)');

/**
 * §2.2.7 — sattva / rajas / tamas.
 *
 * ⚠ **Not derivable from the lord's guṇa, and that is worth stating because it
 * is the obvious wrong guess.** §2.2.7 (rāśi guṇa) and §3.2.10 (graha guṇa) are
 * independent statements about different things, and they disagree for exactly
 * four rāśis: Meṣa and Vṛścika are rājasika under lords (Mars) who are
 * tāmasika, and Mithuna and Kanyā are tāmasika under a lord (Mercury) who is
 * rājasika. Deriving one from the other would have silently changed those four.
 *
 * ◆ Eight of twelve *do* agree, which is exactly why the shortcut is tempting
 * and why the test pins the four that do not.
 */
const RASHI_GUNA = byGroup(12, {
  sattva: [3, 4, 8, 11],
  rajas:  [0, 1, 6, 7],
  tamas:  [2, 5, 9, 10]
}, 'rāśi guṇa (§2.2.7)');

/** §2.2.12 — varṇa, which the classical text derives from the tattva. Asserted below. */
const RASHI_VARNA = byGroup(12, {
  brahmana:  [3, 7, 11],
  kshatriya: [0, 4, 8],
  vaishya:   [1, 5, 9],
  shudra:    [2, 6, 10]
}, 'rāśi varṇa (§2.2.12)');

/** §2.2.8 — direction. */
const RASHI_DIRECTION = byGroup(12, {
  east:  [0, 4, 8],
  south: [1, 5, 9],
  west:  [2, 6, 10],
  north: [3, 7, 11]
}, 'rāśi direction (§2.2.8)');

/**
 * §2.2.11 — how a rāśi rises. Mīna alone rises both ways.
 *
 * ⚠ the classical text footnotes his own term: *pṛṣṭha* means "back", and "rising with the
 * feet" is the scholars' reading he adopts rather than the literal one. The id
 * is kept as `prishthodaya` so the source's word survives the translation.
 */
const RASHI_RISE = byGroup(12, {
  shirshodaya:   [2, 4, 5, 6, 7, 10],
  prishthodaya:  [0, 1, 3, 8, 9],
  ubhayodaya:    [11]
}, 'rāśi rising (§2.2.11)');

/**
 * §2.2.10 — divā (day-strong) and niśā (night-strong).
 *
 * ◆ the classical text states an invariant alongside the lists: "Out of the two rāśis owned by
 * a planet, one is a day sign and one is a night sign." That is a free
 * consistency check on the transcription, and it is asserted at load.
 */
const RASHI_DAY_NIGHT = byGroup(12, {
  nisha: [0, 1, 2, 3, 8, 9],
  diva:  [4, 5, 6, 7, 10, 11]
}, 'rāśi day/night (§2.2.10)');

/**
 * §2.2.6 — the āyurvedic humour of a rāśi, as the classical text assigns it.
 *
 * ⚠ **the classical assignment contradicts a classical definition, and it is recorded
 * unaltered.** He defines vāta as air + ether and kapha as earth + water, then
 * assigns vāta to the *earthy* rāśis and calls the *airy* ones "mixed". The
 * derivation from his definitions would give airy → vāta, earthy → kapha.
 * Either the assignment or the definition is wrong; nothing in this project
 * reads this field yet, and medical work (Phase 11) must settle it against
 * BPHS before using it. See TABLE_DISCREPANCIES.
 */
const RASHI_DOSHA = byGroup(12, {
  pitta: [0, 4, 8],
  vata:  [1, 5, 9],
  kapha: [3, 7, 11],
  mishra: [2, 6, 10]
}, 'rāśi doṣa (§2.2.6)');

/** §2.2.1 — the limbs of Viṣṇu, i.e. the kālapuruṣa mapping. */
const RASHI_LIMB = [
  'head', 'face', 'arms', 'heart', 'stomach', 'hip',
  'below-navel', 'private-parts', 'thighs', 'knees', 'ankles', 'feet'
];

/** §2.2.9 — colours, in a classical words. */
const RASHI_COLOUR = [
  'blood-red', 'white', 'grass-green', 'pale-red', 'white', 'variegated',
  'black', 'reddish-brown', 'grass-husk', 'variegated', 'mongoose-brown', 'cream'
];

/**
 * The twelve rāśis, assembled.
 *
 * `index` is 0-based (A5). Every field carries the section it came from in the
 * comments above; none of it was typed from memory.
 */
export const RASHIS = RASHI_IDS.map((id, i) => Object.freeze({
  index: i,
  id,
  abbr: RASHI_ABBR[i],
  name: Object.freeze(RASHI_NAMES[i]),
  lord: RASHI_LORD[i],
  mobility: RASHI_MOBILITY[i],
  tattva: RASHI_TATTVA[i],
  gender: RASHI_GENDER[i],
  footed: RASHI_FOOTED[i],
  guna: RASHI_GUNA[i],
  varna: RASHI_VARNA[i],
  direction: RASHI_DIRECTION[i],
  rise: RASHI_RISE[i],
  dayNight: RASHI_DAY_NIGHT[i],
  dosha: RASHI_DOSHA[i],
  limb: RASHI_LIMB[i],
  colour: RASHI_COLOUR[i],
  startDeg: i * RASHI_SPAN_DEG,
  endDeg: (i + 1) * RASHI_SPAN_DEG
}));

/** The rāśi object for a 0-based index, wrapping the circle. */
export function rashi(index) {
  if (!Number.isInteger(index)) throw new TypeError(`rashi: index must be an integer, got ${index}`);
  return RASHIS[((index % 12) + 12) % 12];
}

/** The rāśi a sidereal longitude occupies. */
export function rashiAt(siderealLon) {
  return RASHIS[rashiIndexOf(siderealLon)];
}

/** The graha that lords a rāśi. */
export function rashiLord(index) {
  return rashi(index).lord;
}

/**
 * Count of rāśis from `from` to `to`, **inclusive of both**, zodiacally — the
 * śāstra's counting, where "the 7th from Meṣa" is Tulā and the count from a
 * rāśi to itself is 1, never 0.
 *
 * ⚠ This is the single most error-prone operation in chart code. Off-by-one
 * here silently shifts every house-from-house relationship by a sign.
 */
export function rashisFrom(fromIndex, toIndex) {
  const a = ((fromIndex % 12) + 12) % 12;
  const b = ((toIndex % 12) + 12) % 12;
  return ((b - a + 12) % 12) + 1;
}

/** The rāśi `n` places from `fromIndex`, counting inclusively (n = 1 is itself). */
export function rashiNthFrom(fromIndex, n) {
  if (!Number.isInteger(n) || n < 1) throw new RangeError(`rashiNthFrom: n must be ≥ 1, got ${n}`);
  return rashi(fromIndex + n - 1);
}

/* ──────────────────────────────────────────────────────────────────────────
 * The nine grahas
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * The nine, in the order the śāstra lists them: the seven of the vāra cycle,
 * then the two nodes.
 *
 * ⚠ Nine, not seven, and not ten. Rāhu and Ketu are grahas — "seizers" — and
 * Uranus, Neptune and Pluto are not. See A5.
 */
export const GRAHA_IDS = [
  'sun', 'moon', 'mars', 'mercury', 'jupiter', 'venus', 'saturn', 'rahu', 'ketu'
];

/** The seven that have a body and therefore an ephemeris position. */
export const PHYSICAL_GRAHA_IDS = GRAHA_IDS.slice(0, 7);

const GRAHA_NAMES = {
  sun:     { en: 'Sūrya',     sa: 'सूर्य',    hi: 'सूर्य',   mr: 'सूर्य',   western: 'Sun',     symbol: 'Su' },
  moon:    { en: 'Candra',    sa: 'चन्द्र',   hi: 'चंद्र',   mr: 'चंद्र',   western: 'Moon',    symbol: 'Mo' },
  mars:    { en: 'Maṅgala',   sa: 'मङ्गल',    hi: 'मंगल',    mr: 'मंगळ',    western: 'Mars',    symbol: 'Ma' },
  mercury: { en: 'Budha',     sa: 'बुध',      hi: 'बुध',     mr: 'बुध',     western: 'Mercury', symbol: 'Me' },
  jupiter: { en: 'Guru',      sa: 'गुरु',     hi: 'गुरु',    mr: 'गुरु',    western: 'Jupiter', symbol: 'Ju' },
  venus:   { en: 'Śukra',     sa: 'शुक्र',    hi: 'शुक्र',   mr: 'शुक्र',   western: 'Venus',   symbol: 'Ve' },
  saturn:  { en: 'Śani',      sa: 'शनि',      hi: 'शनि',     mr: 'शनि',     western: 'Saturn',  symbol: 'Sa' },
  rahu:    { en: 'Rāhu',      sa: 'राहु',     hi: 'राहु',    mr: 'राहू',    western: 'North node', symbol: 'Ra' },
  ketu:    { en: 'Ketu',      sa: 'केतु',     hi: 'केतु',    mr: 'केतू',    western: 'South node', symbol: 'Ke' }
};

/** Alternative names the sources use interchangeably, for search and display. */
const GRAHA_ALIASES = {
  sun: ['Ravi'], moon: ['Soma'], mars: ['Kuja', 'Aṅgāraka'],
  mercury: [], jupiter: ['Bṛhaspati', 'Devaguru'], venus: [],
  saturn: ['Manda'], rahu: [], ketu: []
};

/**
 * §3.2.2 — natural benefics and malefics.
 *
 * ⚠ **Two of the nine are conditional, and a boolean column loses that.**
 * Mercury is benefic alone or with more benefics and malefic when joined by
 * more malefics; the Moon is benefic waxing (śukla pakṣa) and malefic waning.
 * So `nature` is one of 'benefic' | 'malefic' | 'conditional', and a
 * conditional graha's actual nature is a *function of the chart*, resolved in
 * kundali.js — never read off this table.
 */
const GRAHA_NATURE = {
  sun: 'malefic', moon: 'conditional', mars: 'malefic', mercury: 'conditional',
  jupiter: 'benefic', venus: 'benefic', saturn: 'malefic',
  rahu: 'malefic', ketu: 'malefic'
};

/** How a conditional graha resolves, so the rule lives next to the flag. */
const GRAHA_CONDITION = {
  moon: 'benefic when waxing (śukla pakṣa), malefic when waning (kṛṣṇa pakṣa) — §3.2.2',
  mercury: 'benefic alone or with more benefics, malefic when joined by more malefics — §3.2.2'
};

/**
 * Table 6 — dignities. `deg` is the degree of *deep* exaltation or debilitation
 * within the rāśi, which is the number ṣaḍbala's uccha bala needs (Phase 6).
 *
 * ⚠ **Exaltation is a point, not a sign.** The Sun is exalted in Meṣa, but
 * *deeply* exalted at Meṣa 10°, and uccha bala falls off linearly from there to
 * zero at the debilitation point 180° away. A sign-only table makes every
 * graha's uccha bala wrong by up to the full amount and nothing looks broken.
 *
 * ⚠ Rāhu and Ketu have rāśis in Table 6 but **no degree**, so `deg` is null and
 * uccha bala must not be computed for them from this table.
 */
const DIGNITY = {
  sun:     { own: [4],     exalt: { rashi: 0,  deg: 10 }, debil: { rashi: 6,  deg: 10 }, moolatrikona: { rashi: 4,  from: 0,  to: 20 } },
  moon:    { own: [3],     exalt: { rashi: 1,  deg: 3  }, debil: { rashi: 7,  deg: 3  }, moolatrikona: { rashi: 1,  from: 3,  to: 30 } },
  mars:    { own: [0, 7],  exalt: { rashi: 9,  deg: 28 }, debil: { rashi: 3,  deg: 28 }, moolatrikona: { rashi: 0,  from: 0,  to: 12 } },
  mercury: { own: [2, 5],  exalt: { rashi: 5,  deg: 15 }, debil: { rashi: 11, deg: 15 }, moolatrikona: { rashi: 5,  from: 15, to: 20 } },
  jupiter: { own: [8, 11], exalt: { rashi: 3,  deg: 5  }, debil: { rashi: 9,  deg: 5  }, moolatrikona: { rashi: 8,  from: 0,  to: 10 } },
  venus:   { own: [1, 6],  exalt: { rashi: 11, deg: 27 }, debil: { rashi: 5,  deg: 27 }, moolatrikona: { rashi: 6,  from: 0,  to: 15 } },
  saturn:  { own: [9, 10], exalt: { rashi: 6,  deg: 20 }, debil: { rashi: 0,  deg: 20 }, moolatrikona: { rashi: 10, from: 0,  to: 20 } },
  rahu:    { own: [10],    exalt: { rashi: 2,  deg: null }, debil: { rashi: 8,  deg: null }, moolatrikona: { rashi: 5,  from: 0, to: 30 } },
  ketu:    { own: [7],     exalt: { rashi: 8,  deg: null }, debil: { rashi: 2,  deg: null }, moolatrikona: { rashi: 11, from: 0, to: 30 } }
};

/**
 * §3.2.15 — dig bala. The bhāva in which a graha is directionally strongest.
 *
 * ◆ Stored as a **bhāva number**, not a compass word, because that is what the
 * computation uses; the compass word is derivable and is only for display.
 * a classical phrasing pairs them: east = lagna (1), south = meridian (10),
 * north = nadir (4), west = 7.
 */
const DIG_BALA_BHAVA = {
  mercury: 1, jupiter: 1, sun: 10, mars: 10, moon: 4, venus: 4, saturn: 7,
  rahu: null, ketu: null
};

const DIG_BALA_DIRECTION = { 1: 'east', 4: 'north', 7: 'west', 10: 'south' };

/**
 * §3.2.7 — sex of the grahas, **as the source prints it**.
 *
 * ⚠ the classical text reads: "Sun, Mars and Jupiter are male. Moon and Venus are
 * female. **Saturn and Mercury are female.**" Two consecutive sentences make
 * four grahas female, and the classical reading (BPHS) is that Mercury and
 * Saturn are *napuṁsaka* — neuter. This is almost certainly a defect in the
 * text, but it is not this module's place to overrule the only source on disk,
 * so both readings are carried: `sex` is the classical literal word and
 * `sexClassical` is the napuṁsaka reading. Nothing in Phase 2 reads either.
 * §3.2.7's stated use is predicting the sex of children — Phase 16 — and that
 * phase must settle it. See TABLE_DISCREPANCIES.
 *
 * ⚠ Rāhu and Ketu are absent from §3.2.7 entirely, so they are null rather than
 * guessed.
 */
const GRAHA_SEX = {
  sun: 'male', mars: 'male', jupiter: 'male',
  moon: 'female', venus: 'female',
  saturn: 'female', mercury: 'female',
  rahu: null, ketu: null
};

const GRAHA_SEX_CLASSICAL = {
  sun: 'male', mars: 'male', jupiter: 'male',
  moon: 'female', venus: 'female',
  saturn: 'napumsaka', mercury: 'napumsaka',
  rahu: null, ketu: null
};

/** §3.2.8 — tattva. The Sun shares Mars's fire; the Moon shares Venus's water. */
const GRAHA_TATTVA = {
  mars: 'agni', sun: 'agni', mercury: 'bhu', saturn: 'vayu',
  jupiter: 'akasha', venus: 'jala', moon: 'jala',
  rahu: null, ketu: null
};

/** §3.2.9 — varṇa. the classical text notes the Moon is a king by cabinet and a vaiśya by varṇa. */
const GRAHA_VARNA = {
  jupiter: 'brahmana', venus: 'brahmana', sun: 'kshatriya', mars: 'kshatriya',
  moon: 'vaishya', mercury: 'vaishya', saturn: 'shudra',
  rahu: null, ketu: null
};

/** §3.2.10 — guṇa. */
const GRAHA_GUNA = {
  sun: 'sattva', moon: 'sattva', jupiter: 'sattva',
  mercury: 'rajas', venus: 'rajas',
  mars: 'tamas', saturn: 'tamas',
  rahu: null, ketu: null
};

/** §3.2.4 — colours. the classical text gives Moon and Jupiter the same tawny, and Sun and Mars the same blood-red. */
const GRAHA_COLOUR = {
  sun: 'blood-red', moon: 'tawny', mars: 'blood-red', mercury: 'grass-green',
  jupiter: 'tawny', venus: 'variegated', saturn: 'black',
  rahu: null, ketu: null
};

/** §3.2.3 — what a graha principally governs: the core naisargika kāraka. */
const GRAHA_GOVERNS = {
  sun: 'soul', moon: 'mind', mars: 'strength', mercury: 'speech',
  jupiter: 'knowledge and happiness', venus: 'potency', saturn: 'grief',
  rahu: null, ketu: null
};

/** §3.2.5 — the planetary cabinet. */
const GRAHA_CABINET = {
  sun: 'king', moon: 'king', mars: 'army-chief', mercury: 'prince',
  jupiter: 'minister', venus: 'minister', saturn: 'servant',
  rahu: 'army', ketu: 'army'
};

/** §3.2.6 — presiding deity. Distinct from a nakṣatra's devatā. */
const GRAHA_DEITY = {
  sun: 'Agni', moon: 'Varuṇa', mars: 'Subrahmaṇya', mercury: 'Mahā Viṣṇu',
  jupiter: 'Indra', venus: 'Śacī Devī', saturn: 'Brahmā',
  rahu: null, ketu: null
};

/** §3.2.12 — the sapta dhātu a graha rules. Used by Phase 11, not here. */
const GRAHA_DHATU = {
  sun: 'bones', moon: 'blood', mars: 'marrow', mercury: 'skin',
  jupiter: 'fat', venus: 'semen', saturn: 'muscles',
  rahu: null, ketu: null
};

/** §3.2.14 — taste. */
const GRAHA_TASTE = {
  sun: 'pungent', moon: 'saline', mars: 'bitter', mercury: 'mixed',
  jupiter: 'sweet', venus: 'sour', saturn: 'astringent',
  rahu: null, ketu: null
};

/** §3.2.15 — kāla bala by half of the day. Mercury is strong in both. */
const GRAHA_STRONG_WHEN = {
  moon: 'night', mars: 'night', saturn: 'night',
  sun: 'day', jupiter: 'day', venus: 'day',
  mercury: 'always',
  rahu: null, ketu: null
};

/** Table 12 — the bhāvas a graha stands in for as a graha-lagna reference. */
const GRAHA_SIGNIFIES_BHAVAS = {
  sun: [9, 10, 11], moon: [4, 1, 2, 11, 9], mars: [3], mercury: [6],
  jupiter: [5], venus: [7], saturn: [8, 12],
  rahu: [], ketu: []
};

/** The vāra a graha lords. 0 = Sunday, as `Date#getDay()` numbers it. */
const GRAHA_WEEKDAY = {
  sun: 0, moon: 1, mars: 2, mercury: 3, jupiter: 4, venus: 5, saturn: 6,
  rahu: null, ketu: null
};

/* ── Naisargika maitrī, derived rather than transcribed ─────────────────── */

/**
 * §3.4.1 states the *rule*, not just the result:
 *
 *   "Take the moolatrikona of the planet. Lord of the rasi where it is exalted
 *    is its friend. Lords of 2nd, 4th, 5th, 8th, 9th and 12th rasis from it are
 *    also its natural friends. Lords of other rasis are its natural enemies. If
 *    a planet becomes a friend and an enemy on account of owning two rasis,
 *    then it is a neutral planet."
 *
 * ◆ So the matrix is **computed** from that rule and then checked against the classical text's
 * Table 7, which prints the answer. Both have to agree or the module refuses to
 * load. That is far stronger than transcribing Table 7: a 7 × 7 grid of
 * friend/neutral/enemy is 49 cells, the matrix is **asymmetric** (the Sun holds
 * Mercury neutral while Mercury holds the Sun a friend), and a single flipped
 * cell changes a graha's dignity and therefore its whole strength assessment
 * with nothing looking wrong.
 */
const MAITRI_FRIEND_OFFSETS = [2, 4, 5, 8, 9, 12];

function deriveNaisargikaMaitri() {
  const out = {};
  for (const g of PHYSICAL_GRAHA_IDS) {
    const { moolatrikona, exalt } = DIGNITY[g];
    const friendSigns = new Set(
      MAITRI_FRIEND_OFFSETS.map((n) => rashiNthFrom(moolatrikona.rashi, n).index)
    );
    friendSigns.add(exalt.rashi);

    const asFriend = new Set(), asEnemy = new Set();
    for (let r = 0; r < 12; r++) {
      const lord = RASHI_LORD[r];
      if (lord === g) continue;                       // a graha is not its own friend
      (friendSigns.has(r) ? asFriend : asEnemy).add(lord);
    }

    const row = {};
    for (const p of PHYSICAL_GRAHA_IDS) {
      if (p === g) continue;
      const f = asFriend.has(p), e = asEnemy.has(p);
      // the classical tie-break: friend AND enemy, which can only happen via a graha's
      // two rāśis falling on opposite sides, makes it neutral.
      row[p] = f && e ? 'sama' : f ? 'mitra' : 'satru';
    }
    out[g] = row;
  }
  return out;
}

/** Table 7, transcribed verbatim, purely so the derivation can be checked. */
const TABLE_7 = {
  sun:     { mitra: ['moon', 'mars', 'jupiter'],   sama: ['mercury'],                        satru: ['venus', 'saturn'] },
  moon:    { mitra: ['sun', 'mercury'],            sama: ['mars', 'jupiter', 'venus', 'saturn'], satru: [] },
  mars:    { mitra: ['sun', 'moon', 'jupiter'],    sama: ['venus', 'saturn'],                satru: ['mercury'] },
  mercury: { mitra: ['sun', 'venus'],              sama: ['mars', 'jupiter', 'saturn'],      satru: ['moon'] },
  jupiter: { mitra: ['sun', 'moon', 'mars'],       sama: ['saturn'],                         satru: ['mercury', 'venus'] },
  venus:   { mitra: ['mercury', 'saturn'],         sama: ['mars', 'jupiter'],                satru: ['sun', 'moon'] },
  saturn:  { mitra: ['mercury', 'venus'],          sama: ['jupiter'],                        satru: ['sun', 'moon', 'mars'] }
};

const NAISARGIKA_MAITRI = deriveNaisargikaMaitri();

// Refuse to load on disagreement. A wrong friendship matrix is silent and
// poisons every dignity, every ṣaḍbala and every yoga downstream.
for (const [g, expected] of Object.entries(TABLE_7)) {
  for (const [relation, others] of Object.entries(expected)) {
    for (const p of others) {
      if (NAISARGIKA_MAITRI[g][p] !== relation) {
        throw new Error(
          `naisargika maitrī: §3.4.1 derivation makes ${p} "${NAISARGIKA_MAITRI[g][p]}" of ${g}, ` +
          `but Table 7 prints "${relation}". One of the two is transcribed wrong.`
        );
      }
    }
  }
}

/**
 * The natural relationship `other` holds **towards** `graha`: 'mitra', 'sama'
 * or 'satru'.
 *
 * ⚠ **Asymmetric.** `naisargikaMaitri('sun', 'mercury')` is 'sama' while
 * `naisargikaMaitri('mercury', 'sun')` is 'mitra'. Argument order is the whole
 * meaning; the first argument is whose point of view it is.
 *
 * ⚠ Throws for Rāhu and Ketu. Table 7 has no row for them, and inventing one
 * would put a fabricated relationship into every nodal dignity.
 */
export function naisargikaMaitri(graha, other) {
  const g = String(graha), o = String(other);
  if (g === o) return 'self';
  const row = NAISARGIKA_MAITRI[g];
  if (!row) {
    throw new RangeError(
      `naisargikaMaitri: no natural relationship is defined for "${graha}" — ` +
      'Table 7 covers the seven physical grahas only, and Rāhu/Ketu are deliberately absent'
    );
  }
  const r = row[o];
  if (!r) throw new RangeError(`naisargikaMaitri: unknown graha "${other}"`);
  return r;
}

/** The nine grahas, assembled. */
export const GRAHAS = GRAHA_IDS.map((id, i) => Object.freeze({
  index: i,
  id,
  name: Object.freeze(GRAHA_NAMES[id]),
  aliases: Object.freeze(GRAHA_ALIASES[id]),
  physical: i < 7,
  nature: GRAHA_NATURE[id],
  condition: GRAHA_CONDITION[id] ?? null,
  ownRashis: Object.freeze(DIGNITY[id].own),
  exaltation: Object.freeze(DIGNITY[id].exalt),
  debilitation: Object.freeze(DIGNITY[id].debil),
  moolatrikona: Object.freeze(DIGNITY[id].moolatrikona),
  digBalaBhava: DIG_BALA_BHAVA[id],
  digBalaDirection: DIG_BALA_DIRECTION[DIG_BALA_BHAVA[id]] ?? null,
  sex: GRAHA_SEX[id],
  sexClassical: GRAHA_SEX_CLASSICAL[id],
  tattva: GRAHA_TATTVA[id],
  varna: GRAHA_VARNA[id],
  guna: GRAHA_GUNA[id],
  colour: GRAHA_COLOUR[id],
  governs: GRAHA_GOVERNS[id],
  cabinet: GRAHA_CABINET[id],
  deity: GRAHA_DEITY[id],
  dhatu: GRAHA_DHATU[id],
  taste: GRAHA_TASTE[id],
  strongWhen: GRAHA_STRONG_WHEN[id],
  signifiesBhavas: Object.freeze(GRAHA_SIGNIFIES_BHAVAS[id]),
  weekday: GRAHA_WEEKDAY[id],
  retrogradeAlways: id === 'rahu' || id === 'ketu'
}));

const GRAHA_BY_ID = new Map(GRAHAS.map((g) => [g.id, g]));

/** The graha object for an id. Throws on a typo rather than returning undefined. */
export function graha(id) {
  const g = GRAHA_BY_ID.get(String(id).toLowerCase());
  if (!g) throw new RangeError(`unknown graha "${id}" — expected one of ${GRAHA_IDS.join(', ')}`);
  return g;
}

/**
 * The grahas that lord a rāśi, **including nodal co-lordship**.
 *
 * ⚠ the classical Table 6 gives Rāhu ownership of Kumbha and Ketu ownership of
 * Vṛścika — rāśis already owned by Saturn and Mars. `rashiLord()` returns the
 * single classical lord, which is what house lordship uses; this returns the
 * co-lords as well, which is what a dignity check needs.
 */
export function rashiLords(index) {
  const i = ((index % 12) + 12) % 12;
  const lords = [RASHI_LORD[i]];
  for (const id of ['rahu', 'ketu']) {
    if (DIGNITY[id].own.includes(i)) lords.push(id);
  }
  return lords;
}

/* ──────────────────────────────────────────────────────────────────────────
 * The twenty-seven nakṣatras
 * ────────────────────────────────────────────────────────────────────────── */

const NAKSHATRA_IDS = [
  'ashvini', 'bharani', 'krittika', 'rohini', 'mrigashira', 'ardra',
  'punarvasu', 'pushya', 'ashlesha', 'magha', 'purva-phalguni', 'uttara-phalguni',
  'hasta', 'chitra', 'svati', 'vishakha', 'anuradha', 'jyeshtha',
  'mula', 'purvashadha', 'uttarashadha', 'shravana', 'dhanishtha', 'shatabhisha',
  'purva-bhadrapada', 'uttara-bhadrapada', 'revati'
];

/** `rao` is the spelling Table 2 prints, kept so the source can be searched. */
const NAKSHATRA_NAMES = [
  { en: 'Aśvinī',            sa: 'अश्विनी',          rao: 'Aswini' },
  { en: 'Bharaṇī',           sa: 'भरणी',             rao: 'Bharani' },
  { en: 'Kṛttikā',           sa: 'कृत्तिका',         rao: 'Krittika' },
  { en: 'Rohiṇī',            sa: 'रोहिणी',           rao: 'Rohini' },
  { en: 'Mṛgaśirā',          sa: 'मृगशिरा',          rao: 'Mrigasira' },
  { en: 'Ārdrā',             sa: 'आर्द्रा',          rao: 'Aardra' },
  { en: 'Punarvasu',         sa: 'पुनर्वसु',         rao: 'Punarvasu' },
  { en: 'Puṣya',             sa: 'पुष्य',            rao: 'Pushyami' },
  { en: 'Āśleṣā',            sa: 'आश्लेषा',          rao: 'Aasresha' },
  { en: 'Maghā',             sa: 'मघा',              rao: 'Makha' },
  { en: 'Pūrva Phalgunī',    sa: 'पूर्वा फाल्गुनी',  rao: 'Poorva Phalguni' },
  { en: 'Uttara Phalgunī',   sa: 'उत्तरा फाल्गुनी',  rao: 'Uttara Phalguni' },
  { en: 'Hasta',             sa: 'हस्त',             rao: 'Hasta' },
  { en: 'Citrā',             sa: 'चित्रा',           rao: 'Chitra' },
  { en: 'Svātī',             sa: 'स्वाती',           rao: 'Swaati' },
  { en: 'Viśākhā',           sa: 'विशाखा',           rao: 'Visaakha' },
  { en: 'Anurādhā',          sa: 'अनुराधा',          rao: 'Anooraadha' },
  { en: 'Jyeṣṭhā',           sa: 'ज्येष्ठा',         rao: 'Jyeshtha' },
  { en: 'Mūla',              sa: 'मूल',              rao: 'Moola' },
  { en: 'Pūrvāṣāḍhā',        sa: 'पूर्वाषाढा',       rao: 'Poorvaashaadha' },
  { en: 'Uttarāṣāḍhā',       sa: 'उत्तराषाढा',       rao: 'Uttaraashaadha' },
  { en: 'Śravaṇa',           sa: 'श्रवण',            rao: 'Sravanam' },
  { en: 'Dhaniṣṭhā',         sa: 'धनिष्ठा',          rao: 'Dhanishtha' },
  { en: 'Śatabhiṣā',         sa: 'शतभिषा',           rao: 'Satabhishak' },
  { en: 'Pūrva Bhādrapadā',  sa: 'पूर्वा भाद्रपदा',  rao: 'Poorvaabhaadra' },
  { en: 'Uttara Bhādrapadā', sa: 'उत्तरा भाद्रपदा',  rao: 'Uttaraabhaadra' },
  { en: 'Revatī',            sa: 'रेवती',            rao: 'Revati' }
];

/**
 * Table 2 — the Vimśottarī lord of each nakṣatra, transcribed.
 *
 * ◆ Load-bearing: this sequence *is* the Vimśottarī Daśā (Phase 7). It is also
 * self-checking, because the śāstra's order is a strict 9-cycle repeated three
 * times, and the assertion below confirms the transcription against that cycle.
 */
const NAKSHATRA_LORD = [
  'ketu', 'venus', 'sun', 'moon', 'mars', 'rahu', 'jupiter', 'saturn', 'mercury',
  'ketu', 'venus', 'sun', 'moon', 'mars', 'rahu', 'jupiter', 'saturn', 'mercury',
  'ketu', 'venus', 'sun', 'moon', 'mars', 'rahu', 'jupiter', 'saturn', 'mercury'
];

/** The Vimśottarī cycle, in its own right — Phase 7 needs the order, not just the mapping. */
export const VIMSHOTTARI_CYCLE = Object.freeze([
  'ketu', 'venus', 'sun', 'moon', 'mars', 'rahu', 'jupiter', 'saturn', 'mercury'
]);

/**
 * Table 38 — the Vimśottarī daśā length of each graha, in years, summing to 120.
 *
 * ◆ Phase 7 owns Vimśottarī, but the citation is here with the cycle it belongs
 * to rather than being re-remembered later. The total is asserted below: 120 is
 * what the name *means* (viṁśottara = "one hundred and twenty"), so a mistyped
 * length cannot hide.
 *
 * ⚠ the classical text flags a live controversy about what "a year" means here — solar
 * (365.2425 d), sāvana (360 d), or 360 tithis. He notes most scholars including
 * his guru use solar years, and that **he uses sāvana years throughout the
 * book**, explicitly "not meant to be a recommendation". Phase 7 must therefore
 * make the year length a *setting*, not a constant, and must say which it used —
 * the two differ by about 1.5 % , which is nineteen days in a 20-year Venus
 * daśā and enough to move an antardaśā boundary past a real event.
 */
export const VIMSHOTTARI_YEARS = Object.freeze({
  sun: 6, moon: 10, mars: 7, rahu: 18, jupiter: 16,
  saturn: 19, mercury: 17, ketu: 7, venus: 20
});

{
  const total = Object.values(VIMSHOTTARI_YEARS).reduce((a, b) => a + b, 0);
  if (total !== 120) {
    throw new Error(`Vimśottarī daśā lengths sum to ${total}, not the 120 the name requires`);
  }
  for (const g of VIMSHOTTARI_CYCLE) {
    if (!(g in VIMSHOTTARI_YEARS)) throw new Error(`Vimśottarī: no daśā length for "${g}"`);
  }
}

for (let i = 0; i < 27; i++) {
  if (NAKSHATRA_LORD[i] !== VIMSHOTTARI_CYCLE[i % 9]) {
    throw new Error(
      `nakṣatra lord ${i + 1}: Table 2 gives "${NAKSHATRA_LORD[i]}" but the Vimśottarī ` +
      `9-cycle requires "${VIMSHOTTARI_CYCLE[i % 9]}" — the transcription is shifted`
    );
  }
}

/**
 * ⚠ **The devatā column is contested, and this is the substantive finding of
 * the nakṣatra table.**
 *
 * the classical Table 2 and the Taittirīya Brāhmaṇa list (3.1.1–2, as commonly
 * reproduced) agree for nakṣatras 1–9 and 22–27 and **disagree for 10–21**.
 * Inside that run the classical entry is repeatedly the *classical value of the next
 * nakṣatra* — Pūrva Phalgunī gets Aryaman (classically Uttara Phalgunī's),
 * Hasta gets Viśvakarmā (classically Citrā's), Citrā gets Vāyu (classically
 * Svātī's), and so on for nine of the twelve.
 *
 * ◆ Two pieces of structural evidence that it is the classical column that slipped,
 * rather than the classical list being the odd one out:
 *
 * 1. **Duplicates.** The Taittirīya list has 27 *distinct* devatās. the classical has
 *    four repeats — Sūrya twice (Maghā, Uttara Phalgunī), Indra twice (Svātī,
 *    Anurādhā), Varuṇa twice (Mūla, Śatabhiṣā), Brahmā twice (Rohiṇī,
 *    Uttarāṣāḍhā). A one-row shift through the middle of a column produces
 *    exactly that signature. The count is asserted below.
 *
 * 2. **The shift is visible entry by entry.** For nine of the twelve, the classical text's
 *    devatā is the classical devatā of the *following* nakṣatra:
 *
 *        11 P. Phalgunī   Aryaman     = classical 12 Uttara Phalgunī
 *        12 U. Phalgunī   Sūrya       = classical 13 Hasta (Savitā, the Sun)
 *        13 Hasta         Viśvakarmā  = classical 14 Citrā (Tvaṣṭā ≡ Viśvakarmā)
 *        14 Citrā         Vāyu        = classical 15 Svātī
 *        15 Svātī         Indra       ≈ classical 16 Viśākhā (Indrāgnī)
 *        16 Viśākhā       Mitra       = classical 17 Anurādhā
 *        17 Anurādhā      Indra       = classical 18 Jyeṣṭhā
 *        18 Jyeṣṭhā       Nirṛti      = classical 19 Mūla
 *        19 Mūla          Varuṇa      ≈ classical 20 Pūrvāṣāḍhā (Āpaḥ, the waters)
 *        20 P. Āṣāḍhā     Viśvadeva   = classical 21 Uttarāṣāḍhā
 *
 *    ◆ Entry 12 is the one that makes the case: the classical text prints "Sun" where the
 *    classical next-entry is **Savitā**, which is a name *of* the Sun. A
 *    typesetter shifting a row reproduces that. An independent tradition would
 *    not land on a synonym of the neighbour nine times out of twelve.
 *
 *    The three that do not fit the shift are 10 Maghā (Sūrya for Pitaraḥ),
 *    and 21 Uttarāṣāḍhā (Brahmā for Viśvedevāḥ) — consistent with a shift that
 *    starts and ends messily, which is how such errors usually look.
 *
 * ⚠ Both readings are therefore carried, and `devataContested` marks the run.
 * **Nothing in Phase 2 reads the devatā.** Its uses are remedial (Phase 11) and
 * interpretive (Phase 16), and one of those phases must settle it against a
 * primary text — not against another website, which will have copied one of
 * these two. See TABLE_DISCREPANCIES.
 */
const NAKSHATRA_DEVATA_PRINTED = [
  'Aśvinī Kumāras', 'Yama', 'Agni', 'Brahmā', 'Candra', 'Śiva', 'Aditi',
  'Bṛhaspati', 'Rāhu', 'Sūrya', 'Aryaman', 'Sūrya', 'Viśvakarmā', 'Vāyu',
  'Indra', 'Mitra', 'Indra', 'Nirṛti', 'Varuṇa', 'Viśvadeva', 'Brahmā',
  'Viṣṇu', 'Vasu', 'Varuṇa', 'Ajaikapāda', 'Ahirbudhnya', 'Pūṣā'
];

const NAKSHATRA_DEVATA_CLASSICAL = [
  'Aśvins', 'Yama', 'Agni', 'Prajāpati', 'Soma', 'Rudra', 'Aditi',
  'Bṛhaspati', 'Sarpāḥ', 'Pitaraḥ', 'Bhaga', 'Aryaman', 'Savitā', 'Tvaṣṭā',
  'Vāyu', 'Indrāgnī', 'Mitra', 'Indra', 'Nirṛti', 'Āpaḥ', 'Viśvedevāḥ',
  'Viṣṇu', 'Vasavaḥ', 'Varuṇa', 'Ajaikapāda', 'Ahirbudhnya', 'Pūṣā'
];

/**
 * Where the two columns differ only in wording, not in referent.
 *
 * ◆ This matters for honesty in both directions. Without it, `devataContested`
 * flags Soma vs Candra and Rudra vs Śiva — the same deity under two names — and
 * the finding looks like eighteen disagreements instead of the twelve real ones.
 * Overstating a source's defect is its own kind of inaccuracy.
 *
 * ⚠ Āśleṣā is the judgement call in this list. The printed table gives Rāhu where the
 * Taittirīya list has Sarpāḥ, the serpents; Rāhu *is* the serpent-graha, so it
 * is counted as a synonym rather than an error — but it is the one pair here a
 * reviewer might reasonably move to the other column.
 */
const DEVATA_SYNONYMS = [
  ['Aśvinī Kumāras', 'Aśvins'],
  ['Brahmā', 'Prajāpati'],
  ['Candra', 'Soma'],
  ['Śiva', 'Rudra'],
  ['Vasu', 'Vasavaḥ'],
  ['Rāhu', 'Sarpāḥ']
].map(([a, b]) => `${a}|${b}`);

function devataAgree(printed, classical) {
  return printed === classical || DEVATA_SYNONYMS.includes(`${printed}|${classical}`);
}

// The duplicate signature that identifies which column slipped.
{
  const dup = (a) => a.length - new Set(a).size;
  const printedDup = dup(NAKSHATRA_DEVATA_PRINTED);
  const classicalDup = dup(NAKSHATRA_DEVATA_CLASSICAL);
  if (classicalDup !== 0) {
    throw new Error(
      `nakṣatra devatā: the Taittirīya list should have 27 distinct devatās but ` +
      `${classicalDup} are repeated — this transcription is wrong`
    );
  }
  if (printedDup !== 4) {
    throw new Error(
      `nakṣatra devatā: the classical Table 2 was transcribed with ${printedDup} duplicate devatās, ` +
      'expected exactly 4 (Sūrya, Indra, Varuṇa, Brahmā) — recheck the transcription'
    );
  }
}

/**
 * ⚠ **gaṇa, yoni, nāḍī and rajju are `verified: false`.**
 *
 * None of them appears anywhere in the sources on disk — not in the textbook,
 * not in the 77 lessons. (The lessons' many hits for "nāḍī" are the yogic iḍā /
 * piṅgalā nāḍīs, a different word.) They are here from common reproduction,
 * which is exactly the provenance ayanamsa.js refuses to present as
 * authoritative.
 *
 * ◆ What *is* checked is their structure, and the checks are diagnostic rather
 * than decorative. Each scheme partitions 27 in a fixed way, so a shifted or
 * duplicated cell — the likely transcription error — breaks a count:
 *
 *     gaṇa   9 / 9 / 9
 *     nāḍī   9 / 9 / 9
 *     yoni   13 pairs + 1 singleton; the singleton is nakula (mongoose)
 *     rajju  6 / 6 / 6 / 6 / 3
 *
 * ⚠ What the checks do **not** rule out is a systematically different
 * tradition, and the traditions do differ. So Phase 12 (Guṇa Milan) may not
 * ship a compatibility score on these until they are closed out against a
 * primary text, the same way the four ayanāṁśa anchors are still open.
 */
const NAKSHATRA_GANA = byGroup(27, {
  deva:     [0, 4, 6, 7, 12, 14, 16, 21, 26],
  manushya: [1, 3, 5, 10, 11, 19, 20, 24, 25],
  rakshasa: [2, 8, 9, 13, 15, 17, 18, 22, 23]
}, 'nakṣatra gaṇa (unverified)');

const NAKSHATRA_NADI = byGroup(27, {
  adi:    [0, 5, 6, 11, 12, 17, 18, 23, 24],
  madhya: [1, 4, 7, 10, 13, 16, 19, 22, 25],
  antya:  [2, 3, 8, 9, 14, 15, 20, 21, 26]
}, 'nakṣatra nāḍī (unverified)');

const NAKSHATRA_YONI = byGroup(27, {
  ashva:   [0, 23],   // horse
  gaja:    [1, 26],   // elephant
  mesha:   [2, 7],    // sheep
  sarpa:   [3, 4],    // serpent
  shvana:  [5, 18],   // dog
  bidala:  [6, 8],    // cat
  musha:   [9, 10],   // rat
  go:      [11, 25],  // cow
  mahisha: [12, 14],  // buffalo
  vyaghra: [13, 15],  // tiger
  mriga:   [16, 17],  // deer
  vanara:  [19, 21],  // monkey
  simha:   [22, 24],  // lion
  nakula:  [20]       // mongoose — the one singleton, and sarpa's enemy
}, 'nakṣatra yoni (unverified)');

const NAKSHATRA_YONI_SEX = byGroup(27, {
  male:   [0, 1, 3, 7, 9, 14, 15, 17, 18, 19, 20, 24, 25],
  female: [2, 4, 5, 6, 8, 10, 11, 12, 13, 16, 21, 22, 23, 26]
}, 'nakṣatra yoni sex (unverified)');

/**
 * Rajju, **derived** from the five-fold zigzag rather than transcribed.
 *
 * The scheme runs pāda → kaṭi → nābhi → kaṇṭha → śiras and back down, with a
 * period of 18, which makes śiras half as common as the rest: 6/6/6/6/3.
 * Deriving it means there is nothing to mis-transcribe, and the count is
 * asserted below as a check on the derivation itself.
 */
const RAJJU_ORDER = ['pada', 'kati', 'nabhi', 'kantha', 'shiras', 'kantha', 'nabhi', 'kati', 'pada'];
const NAKSHATRA_RAJJU = Array.from({ length: 27 }, (_, i) => RAJJU_ORDER[i % 9]);

{
  const counts = {};
  for (const r of NAKSHATRA_RAJJU) counts[r] = (counts[r] || 0) + 1;
  const expected = { pada: 6, kati: 6, nabhi: 6, kantha: 6, shiras: 3 };
  for (const [k, v] of Object.entries(expected)) {
    if (counts[k] !== v) {
      throw new Error(`nakṣatra rajju: expected ${v} × ${k}, derived ${counts[k]}`);
    }
  }
}

// The contested run is a claim about the source, so it is asserted, not asserted-at.
{
  const contested = NAKSHATRA_DEVATA_PRINTED
    .map((r, i) => (devataAgree(r, NAKSHATRA_DEVATA_CLASSICAL[i]) ? -1 : i + 1))
    .filter((n) => n > 0);
  const expected = [10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21];
  if (contested.join() !== expected.join()) {
    throw new Error(
      `nakṣatra devatā: the disagreement with the classical Table 2 should be the contiguous run ` +
      `${expected.join('–')}, but is ${contested.join(', ')} — recheck both columns`
    );
  }
}

/** The 27 nakṣatras, assembled. `index` is 0-based; `number` is the 1-based one texts cite. */
export const NAKSHATRAS = NAKSHATRA_IDS.map((id, i) => Object.freeze({
  index: i,
  number: i + 1,
  id,
  name: Object.freeze(NAKSHATRA_NAMES[i]),
  lord: NAKSHATRA_LORD[i],
  devata: NAKSHATRA_DEVATA_CLASSICAL[i],
  devataPrinted: NAKSHATRA_DEVATA_PRINTED[i],
  devataContested: !devataAgree(NAKSHATRA_DEVATA_PRINTED[i], NAKSHATRA_DEVATA_CLASSICAL[i]),
  gana: NAKSHATRA_GANA[i],
  nadi: NAKSHATRA_NADI[i],
  yoni: NAKSHATRA_YONI[i],
  yoniSex: NAKSHATRA_YONI_SEX[i],
  rajju: NAKSHATRA_RAJJU[i],
  verified: Object.freeze({
    lord: true, devata: false, gana: false, nadi: false, yoni: false, rajju: false
  }),
  startDeg: i * NAKSHATRA_SPAN_DEG,
  endDeg: (i + 1) * NAKSHATRA_SPAN_DEG
}));

/**
 * Abhijit — the 28th nakṣatra, kept **out** of the 27-fold division.
 *
 * the classical text: "The last quarter of Uttarashadha is known as Abhijit. However, we
 * consider 27 nakṣatras for all other purposes." It is used in Kota Cakra and
 * Sarvatobhadra Cakra (Phase 9) and in muhūrta (Phase 3).
 *
 * ⚠ It is deliberately not index 27 of NAKSHATRAS. Adding it there would make
 * `NAKSHATRAS.length` 28 and silently corrupt every span, pada and daśā
 * calculation that divides the circle by the array length.
 */
export const ABHIJIT = Object.freeze({
  id: 'abhijit',
  name: Object.freeze({ en: 'Abhijit', sa: 'अभिजित्' }),
  within: 'uttarashadha',
  pada: 4,
  startDeg: 20 * NAKSHATRA_SPAN_DEG + 3 * PADA_SPAN_DEG,
  endDeg: 21 * NAKSHATRA_SPAN_DEG,
  note: 'The 4th pada of Uttarāṣāḍhā. Used in Kota Cakra, Sarvatobhadra Cakra and muhūrta; ' +
        'excluded from the 27-fold zodiacal division. — BPHS / Parāśara §1.3.6'
});

const NAKSHATRA_BY_ID = new Map(NAKSHATRAS.map((n) => [n.id, n]));

/** The nakṣatra object for a 0-based index, wrapping the circle. */
export function nakshatra(index) {
  if (!Number.isInteger(index)) throw new TypeError(`nakshatra: index must be an integer, got ${index}`);
  return NAKSHATRAS[((index % 27) + 27) % 27];
}

/** The nakṣatra object for an id. */
export function nakshatraById(id) {
  const n = NAKSHATRA_BY_ID.get(String(id).toLowerCase());
  if (!n) throw new RangeError(`unknown nakṣatra "${id}"`);
  return n;
}

/* ──────────────────────────────────────────────────────────────────────────
 * The twelve bhāvas
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * ⚠ **`bhavaNumber` is 1-based** (A5), unlike `rashiIndex`. The two are
 * different kinds of thing and the names carry the difference precisely because
 * confusing them is the commonest bug in chart code.
 */

const BHAVA_NAMES = [
  { en: 'Tanu',      sa: 'तनु',       gloss: 'body' },
  { en: 'Dhana',     sa: 'धन',        gloss: 'wealth' },
  { en: 'Sahaja',    sa: 'सहज',       gloss: 'siblings' },
  { en: 'Bandhu',    sa: 'बन्धु',     gloss: 'kin, home' },
  { en: 'Putra',     sa: 'पुत्र',     gloss: 'children' },
  { en: 'Śatru',     sa: 'शत्रु',     gloss: 'enemies' },
  { en: 'Kalatra',   sa: 'कलत्र',     gloss: 'spouse' },
  { en: 'Āyuḥ',      sa: 'आयुः',      gloss: 'longevity' },
  { en: 'Dharma',    sa: 'धर्म',      gloss: 'dharma, father' },
  { en: 'Karma',     sa: 'कर्म',      gloss: 'action, career' },
  { en: 'Lābha',     sa: 'लाभ',       gloss: 'gain' },
  { en: 'Vyaya',     sa: 'व्यय',      gloss: 'loss' }
];

/**
 * §7.4 — the special categories. the classical text lists seven and they **overlap by design**:
 * a bhāva can be a kendra and a trikoṇa at once (the 1st is both), and the 3rd is
 * both an āpoklima and an upacaya.
 *
 * ⚠ So this is *not* a partition and `byGroup` must not be used on it. Each
 * category is its own membership test. Treating them as mutually exclusive —
 * the natural mistake after the rāśi tables above — would drop the 1st house
 * from the trikoṇas or the 6th from the duḥsthānas.
 */
export const BHAVA_CATEGORIES = Object.freeze({
  trikona:   Object.freeze([1, 5, 9]),
  kendra:    Object.freeze([1, 4, 7, 10]),
  panaphara: Object.freeze([2, 5, 8, 11]),
  apoklima:  Object.freeze([3, 6, 9, 12]),
  upachaya:  Object.freeze([3, 6, 10, 11]),
  dusthana:  Object.freeze([6, 8, 12]),
  chaturasra: Object.freeze([4, 8])
});

/**
 * §7.1 — the four-fold purpose classification. Each bhāva belongs to exactly
 * one, so this one *is* a partition.
 */
const BHAVA_PURUSHARTHA = byGroup(12, {
  dharma: [0, 4, 8],
  artha:  [1, 5, 9],
  kama:   [2, 6, 10],
  moksha: [3, 7, 11]
}, 'bhāva puruṣārtha');

/**
 * §7.2 — significations, transcribed from a classical prose in order.
 *
 * ⚠ These are the *rāśi-chart* significations. the classical text is emphatic (§7.1) that a
 * divisional chart recolours them: "the 4th house from lagna in D-16 may mean
 * something and the 4th house from lagna in D-24 may mean something else." So
 * Phase 4 must not reuse this list unqualified.
 */
const BHAVA_SIGNIFICATIONS = [
  ['physical body', 'complexion', 'appearance', 'head', 'intelligence', 'strength',
   'energy', 'fame', 'success', 'nature of birth', 'caste'],
  ['wealth', 'assets', 'family', 'speech', 'eyes', 'mouth', 'face', 'voice', 'food'],
  ['younger co-borns', 'confidants', 'courage', 'mental strength', 'communication skills',
   'creativity', 'throat', 'ears', 'arms', "father's death", 'travels'],
  ['mother', 'vehicles', 'house', 'lands', 'immovable property', 'motherland', 'childhood',
   'education', 'relatives', 'happiness', 'comforts', 'peace', 'state of mind', 'heart'],
  ['children', 'pūrvapuṇya', 'intelligence', 'knowledge and scholarship', 'devotion',
   'mantras', 'stomach', 'digestive system', 'authority', 'fame', 'love', 'affection',
   'emotions', 'judgment', 'speculation'],
  ['enemies', 'service', 'servants', 'relatives', 'mental tension', 'injuries', 'health',
   'diseases', 'agriculture', 'accidents', 'mental affliction', 'hips'],
  ['marriage', 'marital life', 'life partner', 'sex', 'passion', 'long journeys', 'partners',
   'business', 'death', 'the body below the navel'],
  ['longevity', 'debts', 'disease', 'ill-fame', 'inheritance', 'loss of friends',
   'occult studies', 'gifts', 'unearned wealth', 'windfall', 'disgrace', 'secrets', 'genitals'],
  ['father', 'teacher', 'boss', 'fortune', 'religiousness', 'spirituality', 'God',
   'higher studies', 'foreign trips', 'dīkṣā', 'past life', 'grandchildren', 'principles',
   'dharma', 'intuition', 'compassion', 'leadership', 'charity', 'thighs'],
  ['growth', 'profession', 'career', 'karma', 'conduct in society', 'fame', 'honors',
   'awards', 'self-respect', 'dignity', 'knees'],
  ['elder co-borns', 'income', 'gains', 'realization of hopes', 'friends', 'ankles'],
  ['losses', 'expenditure', 'punishment', 'imprisonment', 'hospitalization', 'misfortune',
   'bad habits', 'sleep', 'meditation', 'donation', 'secret enemies', 'left eye', 'feet',
   'residence away from birthplace', 'mokṣa']
];

/**
 * Table 12 — the graha that acts as the natural reference for a bhāva, inverted
 * from the classical planet → houses listing.
 *
 * ⚠ It is **not** a bijection, and a one-kāraka-per-bhāva column would be a
 * fabrication. The Moon signifies five bhāvas (4, 1, 2, 11, 9) and the Sun three
 * (9, 10, 11), so the 9th and the 11th each have *two* candidate references
 * while the 3rd has only Mars.
 *
 * ◆ As it happens the seven rows of Table 12 do cover all twelve bhāvas exactly
 * once between them apart from those two overlaps — so no bhāva comes out with
 * an empty list. That is a property of the table, not something relied on: a
 * bhāva no graha signified would get `[]` rather than a guessed graha.
 */
const BHAVA_KARAKAS = (() => {
  const out = Array.from({ length: 12 }, () => []);
  for (const g of GRAHA_IDS) {
    for (const b of GRAHA_SIGNIFIES_BHAVAS[g]) out[b - 1].push(g);
  }
  return out;
})();

/** The twelve bhāvas, assembled. */
export const BHAVAS = Array.from({ length: 12 }, (_, i) => Object.freeze({
  number: i + 1,
  name: Object.freeze(BHAVA_NAMES[i]),
  purushartha: BHAVA_PURUSHARTHA[i],
  categories: Object.freeze(
    Object.entries(BHAVA_CATEGORIES)
      .filter(([, members]) => members.includes(i + 1))
      .map(([k]) => k)
  ),
  karakas: Object.freeze(BHAVA_KARAKAS[i]),
  significations: Object.freeze(BHAVA_SIGNIFICATIONS[i])
}));

/** The bhāva object for a 1-based number, wrapping the circle. */
export function bhava(number) {
  if (!Number.isInteger(number)) throw new TypeError(`bhava: number must be an integer, got ${number}`);
  return BHAVAS[(((number - 1) % 12) + 12) % 12];
}

/**
 * Count of bhāvas from `from` to `to` inclusive — the śāstra's counting, where
 * the count from a bhāva to itself is 1.
 *
 * ◆ the classical §7.4 worked example is the test: the 5th from the 3rd is the 7th.
 */
export function bhavasFrom(fromNumber, toNumber) {
  const a = (((fromNumber - 1) % 12) + 12) % 12;
  const b = (((toNumber - 1) % 12) + 12) % 12;
  return ((b - a + 12) % 12) + 1;
}

/** The bhāva `n` places from `fromNumber`, counting inclusively (n = 1 is itself). */
export function bhavaNthFrom(fromNumber, n) {
  if (!Number.isInteger(n) || n < 1) throw new RangeError(`bhavaNthFrom: n must be ≥ 1, got ${n}`);
  return (((fromNumber - 1 + n - 1) % 12) + 12) % 12 + 1;
}

/** Is `number` in the named §7.4 category? */
export function isBhavaCategory(number, category) {
  const members = BHAVA_CATEGORIES[category];
  if (!members) {
    throw new RangeError(
      `unknown bhāva category "${category}" — expected one of ${Object.keys(BHAVA_CATEGORIES).join(', ')}`
    );
  }
  return members.includes((((number - 1) % 12) + 12) % 12 + 1);
}

/* ──────────────────────────────────────────────────────────────────────────
 * Dignity
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * The degree ranges over which exaltation, rather than mūlatrikoṇa or own sign,
 * applies — from the classical §3.3 special points (2) and (4).
 *
 * ◆ Only the Moon and Mercury have one. For every other graha the source treats
 * the whole exaltation rāśi as exaltation, with the listed degree marking the
 * point of *deepest* exaltation for uccha bala rather than a boundary. Writing
 * ranges for the others would be inventing boundaries the source does not give.
 */
const EXALT_RANGE = {
  moon: { from: 0, to: 3 },     // §3.3 (2)
  mercury: { from: 0, to: 15 }  // §3.3 (4)
};

/**
 * The dignity of a graha at a **sidereal** longitude.
 *
 * Returns the strongest applicable label, in the order the śāstra ranks them:
 *
 *     'exalted' > 'moolatrikona' > 'ownSign' > 'friend' > 'neutral' > 'enemy' > 'debilitated'
 *
 * ⚠ the classical §3.3 "special points" make exaltation and mūlatrikoṇa **degree
 * ranges inside one rāśi, not the whole rāśi**, and the ranges do not tile it:
 *
 *     Moon in Vṛṣabha   0°–3°   exalted, 3°–30° mūlatrikoṇa
 *     Mercury in Kanyā  0°–15°  exalted, 15°–20° mūlatrikoṇa, 20°–30° own
 *     Sun in Siṁha      0°–20°  mūlatrikoṇa, 20°–30° own
 *
 * So a Sun at Siṁha 25° is in its **own sign and not its mūlatrikoṇa**, and a
 * Mercury at Kanyā 25° is neither exalted nor mūlatrikoṇa. A sign-level dignity
 * check calls all three "exalted" and hands Phase 6 a saptavargaja bala that is
 * wrong in a way no output reveals.
 *
 * ⚠ Rāhu and Ketu have rāśis in Table 6 but no degrees, so their exaltation is
 * read at sign level and `deepDegree` is null. That asymmetry is the source's,
 * not a shortcut.
 */
export function dignityOf(grahaId, siderealLon) {
  const g = graha(grahaId);
  const r = rashiIndexOf(siderealLon);
  const deg = degreesInRashi(siderealLon);
  const d = DIGNITY[g.id];

  const result = (label, extra = {}) => ({
    graha: g.id, rashiIndex: r, degreesInRashi: deg, dignity: label, ...extra
  });

  // Exaltation, as a range where the source gives one.
  if (r === d.exalt.rashi) {
    const range = EXALT_RANGE[g.id];
    if (!range || (deg >= range.from && deg < range.to)) {
      return result('exalted', { deepDegree: d.exalt.deg });
    }
  }
  if (r === d.debil.rashi) {
    return result('debilitated', { deepDegree: d.debil.deg });
  }
  if (r === d.moolatrikona.rashi && deg >= d.moolatrikona.from && deg < d.moolatrikona.to) {
    return result('moolatrikona');
  }
  if (d.own.includes(r)) return result('ownSign');

  // Otherwise the dignity is the relationship with the rāśi's lord.
  const lord = RASHI_LORD[r];
  if (lord === g.id) return result('ownSign');
  let relation;
  try {
    relation = naisargikaMaitri(g.id, lord);
  } catch {
    // Rāhu and Ketu have no row in Table 7; say so rather than invent one.
    return result('unrated', { reason: 'Table 7 defines no natural relationship for this graha' });
  }
  return result({ mitra: 'friend', sama: 'neutral', satru: 'enemy' }[relation], { rashiLord: lord });
}


/* ──────────────────────────────────────────────────────────────────────────
 * Pañcāṅga tables — BPHS / Parāśara §1.3.8–1.3.11, Tables 3–5
 * ────────────────────────────────────────────────────────────────────────── */

/** A tithi is 12° of Moon−Sun elongation. §1.3.8.1. */
export const TITHI_SPAN_DEG = 12;

/** A karaṇa is half a tithi, 6°. §1.3.10. */
export const KARANA_SPAN_DEG = 6;

/** A yoga is 13°20′ of Sun+Moon sum — the same span as a nakṣatra. §1.3.9. */
export const YOGA_SPAN_DEG = NAKSHATRA_SPAN_DEG;

/**
 * Table 3 — the fifteen tithi names cycling through both pakṣas.
 *
 * Index 0 = Pratipadā … 13 = Caturdaśī, then Pūrṇimā (śukla 15) / Amāvasyā
 * (kṛṣṇa 15) share slot 14 under different names. The *running* tithi is 1–30;
 * `tithiName(n)` maps that to pakṣa + name.
 */
export const TITHI_NAMES = Object.freeze([
  'pratipada', 'dwitiya', 'tritiya', 'chaturthi', 'panchami',
  'shashti', 'saptami', 'ashtami', 'navami', 'dasami',
  'ekadasi', 'dwadasi', 'trayodasi', 'chaturdasi', 'purnima'
]);

/** Amāvasyā replaces Pūrṇimā as the name of tithi 30. */
export const TITHI_AMAVASYA = 'amavasya';

/** The graha that lords each of the fifteen tithi names. Table 3. */
export const TITHI_LORD = Object.freeze([
  'sun', 'moon', 'mars', 'mercury', 'jupiter',
  'venus', 'saturn', 'rahu', 'sun', 'moon',
  'mars', 'mercury', 'jupiter', 'venus', 'saturn'
]);
// ⚠ Amāvasyā (tithi 30) is lorded by Rāhu per Table 3, not by Saturn (Pūrṇimā).

/**
 * Table 5 — the twenty-seven yogas. Index 0 = Viṣkambha.
 *
 * ◆ Same count and span as the nakṣatras, but a different cycle: it is the
 * *sum* of Sun and Moon, not the Moon alone.
 */
export const YOGA_IDS = Object.freeze([
  'vishkambha', 'preeti', 'aayushmaan', 'saubhaagya', 'sobhana',
  'atiganda', 'sukarman', 'dhriti', 'shoola', 'ganda',
  'vriddhi', 'dhruva', 'vyaaghaata', 'harshana', 'vajra',
  'siddhi', 'vyatipaata', 'variyan', 'parigha', 'shiva',
  'siddha', 'saadhya', 'subha', 'sukla', 'brahma',
  'indra', 'vaidhriti'
]);

const YOGA_MEANING = Object.freeze({
  vishkambha: 'door bolt / supporting pillar',
  preeti: 'love / affection',
  aayushmaan: 'long-lived',
  saubhaagya: 'long life of spouse (good fortune)',
  sobhana: 'splendid, bright',
  atiganda: 'great danger',
  sukarman: 'one with good deeds',
  dhriti: 'firmness',
  shoola: "Śiva's weapon of destruction (pain)",
  ganda: 'danger',
  vriddhi: 'growth',
  dhruva: 'fixed, constant',
  vyaaghaata: 'great blow',
  harshana: 'cheerful',
  vajra: 'diamond (strong)',
  siddhi: 'accomplishment',
  vyatipaata: 'great fall',
  variyan: 'chief / best',
  parigha: 'obstacle / hindrance',
  shiva: 'Lord Śiva (purity)',
  siddha: 'accomplished / ready',
  saadhya: 'possible',
  subha: 'auspicious',
  sukla: 'white, bright',
  brahma: 'creator (good knowledge and purity)',
  indra: 'ruler of gods',
  vaidhriti: 'a class of gods'
});

export const YOGAS = YOGA_IDS.map((id, i) => Object.freeze({
  index: i, number: i + 1, id, meaning: YOGA_MEANING[id]
}));

export function yoga(index) {
  const y = YOGAS[((index % 27) + 27) % 27];
  if (!y) throw new RangeError(`yoga: index ${index} out of range`);
  return y;
}

/**
 * §1.3.10 — the eleven karaṇas.
 *
 * The movable seven (Bava…Viṣṭi) repeat eight times from the *second* half of
 * tithi 1 through the first half of tithi 29. The four fixed ones occupy the
 * remaining four half-tithis of the month: Kimstughna (1st half of tithi 1),
 * then Śakuna, Catuṣpāda, Nāga (2nd half of 29 and both halves of 30).
 */
export const KARANA_MOVABLE = Object.freeze([
  'bava', 'balava', 'kaulava', 'taitula', 'garija', 'vanija', 'vishti'
]);
export const KARANA_FIXED = Object.freeze([
  'sakuna', 'chatushpada', 'naga', 'kimstughna'
]);

/**
 * Table 4 — lunar month names by the rāśi of the Sun–Moon conjunction that
 * *starts* the month. That is the **amānta** naming. Index = rāśi of the
 * conjunction (0 = Meṣa → Vaiśākha; 11 = Mīna → Caitra).
 *
 * ⚠ the classical table lists Pisces first; the array is indexed by rāśi number so
 * lookups do not have to rotate.
 */
export const LUNAR_MONTH_BY_CONJUNCTION_RASHI = Object.freeze([
  'vaishakha',   // 0 Meṣa
  'jyeshtha',    // 1 Vṛṣabha
  'aashadha',    // 2 Mithuna
  'sraavana',    // 3 Karka
  'bhaadrapada', // 4 Siṁha
  'aaswayuja',   // 5 Kanyā
  'kaarteeka',   // 6 Tulā
  'maargasira',  // 7 Vṛścika
  'pushya',      // 8 Dhanu
  'maagha',      // 9 Makara
  'phaalguna',   // 10 Kumbha
  'chaitra'      // 11 Mīna
]);

/**
 * The vāra lords in weekday order, 0 = Sunday. Same as GRAHA_WEEKDAY inverted.
 * Chaldean hora order from §1.3.11: Sa → Ju → Ma → Su → Ve → Me → Mo.
 */
export const VARA_LORDS = Object.freeze([
  'sun', 'moon', 'mars', 'mercury', 'jupiter', 'venus', 'saturn'
]);

/** Chaldean order of decreasing geocentric speed — the hora cycle. §1.3.11. */
export const CHALDEAN_ORDER = Object.freeze([
  'saturn', 'jupiter', 'mars', 'sun', 'venus', 'mercury', 'moon'
]);

/**
 * Rāhu Kāla is the nth eighth of the day (sunrise→sunset). Plan §3.1:
 * Sun 8, Mon 2, Tue 7, Wed 5, Thu 6, Fri 4, Sat 3. Stored 1-based.
 */
export const RAHU_KALA_EIGHTH = Object.freeze([8, 2, 7, 5, 6, 4, 3]);

/**
 * Yamagaṇḍa eighths — traditional pañcāṅga sequence (not in the classical text). Cited as
 * common almanac practice; Phase 3 acceptance checks structure, not a named
 * almanac row.
 */
export const YAMAGANDA_EIGHTH = Object.freeze([5, 4, 3, 2, 1, 7, 6]);

/** Gulika Kāla eighths — traditional, same provenance note as Yamagaṇḍa. */
export const GULIKA_EIGHTH = Object.freeze([7, 6, 5, 4, 3, 2, 1]);

/**
 * Day Choghaḍiyā sequences by vāra (0 = Sunday), eight equal parts of the day.
 * Night sequences follow below. Good: amrit, shubh, labh, chal. Bad: rog, kaal, udveg.
 */
export const CHOGHADIYA_DAY = Object.freeze([
  ['udveg', 'chal', 'labh', 'amrit', 'kaal', 'shubh', 'rog', 'udveg'],       // Sun
  ['amrit', 'kaal', 'shubh', 'rog', 'udveg', 'chal', 'labh', 'amrit'],       // Mon
  ['rog', 'udveg', 'chal', 'labh', 'amrit', 'kaal', 'shubh', 'rog'],         // Tue
  ['labh', 'amrit', 'kaal', 'shubh', 'rog', 'udveg', 'chal', 'labh'],        // Wed
  ['shubh', 'rog', 'udveg', 'chal', 'labh', 'amrit', 'kaal', 'shubh'],       // Thu
  ['chal', 'labh', 'amrit', 'kaal', 'shubh', 'rog', 'udveg', 'chal'],        // Fri
  ['kaal', 'shubh', 'rog', 'udveg', 'chal', 'labh', 'amrit', 'kaal']         // Sat
]);

export const CHOGHADIYA_NIGHT = Object.freeze([
  ['shubh', 'amrit', 'chal', 'rog', 'kaal', 'labh', 'udveg', 'shubh'],       // Sun
  ['chal', 'rog', 'kaal', 'labh', 'udveg', 'shubh', 'amrit', 'chal'],        // Mon
  ['kaal', 'labh', 'udveg', 'shubh', 'amrit', 'chal', 'rog', 'kaal'],        // Tue
  ['udveg', 'shubh', 'amrit', 'chal', 'rog', 'kaal', 'labh', 'udveg'],       // Wed
  ['amrit', 'chal', 'rog', 'kaal', 'labh', 'udveg', 'shubh', 'amrit'],       // Thu
  ['rog', 'kaal', 'labh', 'udveg', 'shubh', 'amrit', 'chal', 'rog'],         // Fri
  ['labh', 'udveg', 'shubh', 'amrit', 'chal', 'rog', 'kaal', 'labh']         // Sat
]);

export const CHOGHADIYA_QUALITY = Object.freeze({
  amrit: 'good', shubh: 'good', labh: 'good', chal: 'good',
  rog: 'bad', kaal: 'bad', udveg: 'bad'
});

/**
 * The thirty muhūrtas of a civil day, counted from sunrise. Classical names
 * used across Muhūrta Cintāmaṇi traditions; Abhijit is the 8th (index 7).
 *
 * ⚠ Quality labels are the common electional reading, not a claim that every
 * school agrees. Abhijit's Wednesday exception is surfaced as a named variant
 * in muhurta.js, not silently applied.
 */
export const MUHURTA_IDS = Object.freeze([
  'rudra', 'ahi', 'mitra', 'pitru', 'vasu',
  'vara', 'vishvedeva', 'abhijit', 'satamukhi', 'puruhuta',
  'vahni', 'naktanakara', 'varuna', 'aryama', 'bhaga',
  'girisha', 'ajapada', 'ahirbudhnya', 'pushya', 'ashvini',
  'yama', 'agni', 'vidhatri', 'kanda', 'aditi',
  'jiva', 'vishnu', 'dyumadgadyuti', 'brahma', 'samudra'
]);

/** Inauspicious by default; the rest are treated as usable. */
export const MUHURTA_INAUSPICIOUS = Object.freeze(new Set([
  'rudra', 'ahi', 'naktanakara', 'yama', 'agni'
]));

/**
 * Pakṣa + display name for a 1-based tithi number (1–30).
 *
 * ◆ Boundary rule matches A5 / divisionIndex: elongation 0° is the *start* of
 * Pratipadā (tithi 1), elongation 12° is the start of Dvitīyā, and so on.
 * Amāvasyā is tithi 30; Pūrṇimā is tithi 15.
 */
export function tithiFromNumber(n) {
  const t = ((Math.trunc(n) - 1) % 30 + 30) % 30 + 1;
  const paksha = t <= 15 ? 'shukla' : 'krishna';
  const nameIndex = (t - 1) % 15;
  const name = t === 30 ? TITHI_AMAVASYA : TITHI_NAMES[nameIndex];
  const lord = t === 30 ? 'rahu' : TITHI_LORD[nameIndex];
  return Object.freeze({ number: t, paksha, name, lord, nameIndex });
}

/* ──────────────────────────────────────────────────────────────────────────
 * What a later phase has to settle
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * The open provenance questions in this file, in one place, with the phase that
 * owns each. ◆ Exported deliberately: `tools/check-accuracy.mjs` prints it, so
 * an unresolved question is visible on every accuracy run rather than buried in
 * a comment.
 */
export const TABLE_DISCREPANCIES = Object.freeze([
  Object.freeze({
    id: 'mars-moolatrikona-typo',
    where: 'BPHS / Parāśara §3.3, special point (3)',
    says: '"Mars gives the results of being in moolatrikona in the first 12º of Le"',
    problem: 'Table 6 on the facing page gives Mars\'s mūlatrikoṇa as Ar, not Le. Mars does not '
           + 'own Le and has no dignity there at all, so (3) contradicts Table 6 outright.',
    resolution: 'Table 6 is followed: mūlatrikoṇa Meṣa 0°–12°. A plain typesetting slip — '
              + 'the six neighbouring special points all name the rāśi Table 6 names.',
    settled: true,
    phase: 2
  }),
  Object.freeze({
    id: 'graha-sex-saturn-mercury',
    where: 'BPHS / Parāśara §3.2.7',
    says: '"Moon and Venus are female. Saturn and Mercury are female."',
    problem: 'Two consecutive sentences make four grahas female and leave none neuter. '
           + 'BPHS makes Mercury and Saturn napuṁsaka.',
    resolution: 'Unsettled. Both readings are carried as `sex` and `sexClassical`. Nothing '
              + 'reads either yet; §3.2.7\'s stated use is predicting the sex of children.',
    settled: false,
    phase: 16
  }),
  Object.freeze({
    id: 'rashi-dosha-vata-kapha',
    where: 'BPHS / Parāśara §2.2.6',
    says: 'earthy rāśis are vāta; airy rāśis are "mixed"',
    problem: 'His own definitions in the same section make vāta = air + ether and '
           + 'kapha = earth + water, which gives airy → vāta and earthy → kapha. The '
           + 'assignment and the definition cannot both be right.',
    resolution: 'Unsettled. The assignment is carried verbatim as `dosha`. Nothing reads it.',
    settled: false,
    phase: 11
  }),
  Object.freeze({
    id: 'nakshatra-devata-shift',
    where: 'classical table 2, devatā column',
    says: 'devatās for nakṣatras 10–21 that disagree with the Taittirīya Brāhmaṇa list',
    problem: 'For nine of the twelve, the classical text\'s entry is the classical devatā of the *following* '
           + 'nakṣatra, and his column repeats four devatās where the classical list repeats '
           + 'none — the signature of a one-row shift.',
    resolution: 'Unsettled. The Taittirīya list is `devata`, the shifted printed column is `devataPrinted`, and '
              + '`devataContested` marks the run. Must be closed against a primary Vedic text.',
    settled: false,
    phase: 11
  }),
  Object.freeze({
    id: 'bhava-lagna-example-rate',
    where: 'BPHS / Parāśara §5.2, Example 7',
    says: '"Bhavalagna moves at the rate of 1° per 4 minutes" — then adds 766 minutes as 766° and prints Pisces 10°17′',
    problem: 'The example advances BL 1° per minute, four times the rate stated in the same '
           + 'section. Horā and Ghaṭī lagna (Examples 8, 9) agree with their stated rates; BL does not.',
    resolution: 'Unsettled. Following plan 05 ("implement from the worked example") the default is the '
              + 'example\'s 1° per minute, so Example 7 reproduces; the prose rate 0°15′ per minute (Siṁha 5°47′) '
              + 'is kept as LAGNA_RATE_PROSE_BHAVA. the classical text\'s Ch. 20 ("Hora lagna moves twice as fast as lagna") '
              + 'supports the prose rate. Both are pinned in tests/jyotish/special-lagnas.test.js. the classical text says BL is '
              + '"not used in this book".',
    settled: false,
    phase: 5
  }),
  Object.freeze({
    id: 'upaketu-exercise-7-answer',
    where: 'BPHS / Parāśara §4.4, answer to Exercise 7',
    says: 'Upaketu "At 19°1′ from the start of Sc" for Sun at 13°19′ Ta',
    problem: 'Table 9 gives Upaketu = Sun − 30°, which is Aries 13°19′; the other four answers in the '
           + 'same key match the table exactly. 19°1′ Sc is not reached by either route in Table 9.',
    resolution: 'Table 9 is followed, and its Sun − 30° invariant is asserted on every call. The key is '
              + 'treated as a misprint.',
    settled: true,
    phase: 5
  }),
  Object.freeze({
    id: 'special-lagnas-uncited',
    where: 'Indu lagna, Varṇada lagna, Prāṇapada',
    says: 'nothing — BPHS / Parāśara §5.7: the remaining special lagnas "are beyond the scope of this book"',
    problem: 'None of the three is defined in any source on disk. They are implemented from the Parāśarī '
           + 'rules as commonly stated, which is provenance this project does not accept as authoritative.',
    resolution: 'Unsettled. Each result carries verified: false. Must be checked against JHora (plan 05 '
              + 'acceptance) or a primary text before being presented as anything but provisional.',
    settled: false,
    phase: 5
  }),
  Object.freeze({
    id: 'guna-milan-tables-uncited',
    where: 'gaṇa, yoni, nāḍī',
    says: 'nothing — these appear in no source on disk',
    problem: 'The textbook and all 77 lessons are silent on them. They are here from common '
           + 'reproduction, which is the provenance ayanamsa.js refuses to call authoritative.',
    resolution: 'Structure is checked (9/9/9, 9/9/9, 13 pairs + nakula). A systematically '
              + 'different tradition would pass those checks, so Guṇa Milan may not ship a '
              + 'score until they are closed against a primary text.',
    settled: false,
    phase: 12
  }),
  Object.freeze({
    id: 'table-28-kanya-multiplier',
    where: 'classical table 28, rāśi multipliers',
    says: 'Kanyā (Vi) 6',
    problem: 'the classical text\'s own Exercise 22 answers (rāśi piṇḍa of all seven grahas of Chart 7) reproduce '
           + 'only with Kanyā = 5, which is also the BPHS figure. With 6, every graha with rekhās in Kanyā '
           + 'is high by that count. The other eleven multipliers are unaffected.',
    resolution: 'Kanyā = 5 is used (RASHI_MULTIPLIERS in ashtakavarga.js). The tests pin all seven '
              + 'Exercise 22 piṇḍas, which is what makes this a finding and not a preference.',
    settled: true,
    phase: 6
  }),
  Object.freeze({
    id: 'ekadhipatya-rules-underspecified',
    where: 'BPHS / Parāśara §12.7.2, ekādhipatya śodhana rules (2)–(3)',
    says: '"occupied by a planet"; (3a) "lower value" → 0, (3b) "higher value" → the other\'s',
    problem: 'Neither says whether the lagna\'s rāśi is occupied, nor what happens on a tie in (3).',
    resolution: 'Both read from his Exercise 22 answers, which reproduce for all seven grahas only when '
              + 'the lagna\'s rāśi counts as occupied and a tie in (3) gives zero. Pinned in '
              + 'tests/jyotish/ashtakavarga.test.js.',
    settled: true,
    phase: 6
  }),
  Object.freeze({
    id: 'shadbala-uncited',
    where: 'Ṣaḍbala — all six balas',
    says: 'nothing — BPHS / Parāśara §15.3: "Explaining the computation of shadbalas is beyond the scope of this book"',
    problem: 'No source on disk gives the formulas, the constants, or a worked example. They are implemented '
           + 'from BPHS ch. 27 and Raman\'s Graha and Bhava Balas as commonly stated, which is provenance '
           + 'this project does not accept as authoritative. The variants are many (ayana, ceṣṭā, abda / '
           + 'māsa, yuddha) and each is named in the result.',
    resolution: 'Unsettled. shadbala() returns verified: false. Must be cross-checked against JHora on ten '
              + 'charts (plan 06 acceptance) before any ṣaḍbala figure is presented as more than provisional.',
    settled: false,
    phase: 6
  })
]);

/** The unsettled subset — what `npm run accuracy` warns about. */
export function openDiscrepancies() {
  return TABLE_DISCREPANCIES.filter((d) => !d.settled);
}
